import React, { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';

const DISMISSED_KEY = 'koda_update_dismissed_session';

function isWebsiteHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'kodacare.pet' || h === 'www.kodacare.pet' || h.includes('kodacare-website');
}

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      // Poll every 60 s so updates are detected promptly
      if (r) setInterval(() => r.update(), 60_000);
    },
  });

  // If the user already dismissed this banner in the current session, don't show again.
  // Exception: keep it visible while update is actively running so the loader can show.
  const wasDismissed = sessionStorage.getItem(DISMISSED_KEY) === 'true';

  const [updating, setUpdating] = useState(false);

  if (!needRefresh || (wasDismissed && !updating) || isWebsiteHost()) return null;

  const handleUpdate = () => {
    setUpdating(true);
    // Mark dismissed so the banner doesn't reappear after refresh.
    // Do not hide immediately: keep the dialog visible so loading animation
    // can run until controllerchange/fallback reload fires.
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    // Listen for the SW controller swap, then reload so the new cache is used.
    // This is the reliable path on Android where postMessage-based reload can stall.
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => window.location.reload(),
      { once: true }
    );
    // Tell the waiting SW to skip waiting and activate immediately.
    void updateServiceWorker(true);
    // Hard fallback in case controllerchange never fires (e.g. no previous SW).
    setTimeout(() => window.location.reload(), 4000);
  };

  const handleDismiss = () => {
    // Suppress for the rest of this session; will re-prompt on next app open
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setNeedRefresh(false);
  };

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[400] w-[calc(100%-2rem)] max-w-sm">
      <div className="flex items-center gap-3 bg-[#0d2744] text-white text-sm font-medium px-4 py-3 rounded-2xl shadow-2xl">
        <RefreshCw size={16} className="shrink-0 text-primary-brown" />
        <span className="flex-1 leading-snug">A new version of KodaCare is ready</span>
        <button
          onClick={handleUpdate}
          disabled={updating}
          className="shrink-0 bg-primary-brown hover:opacity-90 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-opacity flex items-center gap-1.5"
        >
          <RefreshCw size={12} className={updating ? 'animate-spin' : ''} />
          {updating ? 'Updating…' : 'Update'}
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          disabled={updating}
          className="shrink-0 text-white/50 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
